import React, { createContext, useContext, useState, useEffect } from 'react';
import { Customer, ViewType, User, Appointment, Report, UserProfileUpdate } from '@/types';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';

interface AppContextType {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  currentUser: User | null;
  customers: Customer[];
  appointments: Appointment[];
  reports: Report[];
  currentView: ViewType;
  setCurrentView: (view: ViewType) => void;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  saveCustomer: (customer: Omit<Customer, 'id'>) => Promise<boolean>;
  updateCustomer: (id: string, customer: Omit<Customer, 'id'>) => Promise<boolean>;
  deleteCustomer: (id: string) => Promise<boolean>;
  fetchData: (user?: User) => void;
  saveAppointment: (appointment: Omit<Appointment, 'id'>) => Promise<boolean>;
  saveReport: (report: Omit<Report, 'id' | 'createdAt'>) => Promise<boolean>;
  updateUserProfile: (profile: UserProfileUpdate) => Promise<boolean>;
  deleteReport: (id: string) => Promise<boolean>;
  loading: boolean;
}


const AppContext = createContext<AppContextType>({} as AppContextType);

export const useAppContext = () => useContext(AppContext);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [currentView, setCurrentView] = useState<ViewType>('calendar');
  const [loading, setLoading] = useState(false);

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  const mockUsers = [
    { id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890', name: 'Admin User', email: 'admin@winery.com', role: 'admin' as const, profilePicture: '' },
    { id: 'b2c3d4e5-f6a7-8901-bcde-f23456789012', name: 'John Sales', email: 'john@winery.com', role: 'salesman' as const, profilePicture: '' }
  ];

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      // Query the database for the user with matching email and password
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .eq('password', password)
        .single();
      
      if (error || !userData) {
        toast({ title: 'Login Failed', description: 'Invalid email or password', variant: 'destructive' });
        return false;
      }
      
      const user: User = {
        id: userData.id,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        profilePicture: userData.profile_picture || ''
      };
      
      setCurrentUser(user);
      localStorage.setItem('crm_current_user', JSON.stringify(user));
      toast({ title: 'Success', description: `Welcome back, ${user.name}!` });
      await fetchData(user);
      return true;
    } catch (err) {
      toast({ title: 'Error', description: 'Login failed', variant: 'destructive' });
      return false;
    } finally {
      setLoading(false);
    }
  };



  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('crm_current_user');
    setCustomers([]);
    setReports([]);
    setCurrentView('calendar');
  };

  const fetchData = async (user?: User) => {
    try {
      setLoading(true);
      
      // Use the passed user or fall back to currentUser
      const activeUser = user || currentUser;
      
      const { data: customerData } = await supabase.from('customers').select('*').order('business_name');
      if (customerData) {
        const formattedCustomers: Customer[] = customerData.map(c => ({
          id: c.id,
          businessName: c.business_name || '',
          contactPerson: c.contact_person || '',
          phone: c.phone || '',
          email: c.email || '',
          address: c.address || '',
          businessType: c.business_type || '',
          notes: c.notes || '',
          salesmanId: c.salesman_id || ''
        }));
        setCustomers(formattedCustomers);
      }

      // Fetch reports - filter based on user role
      let reportQuery = supabase.from('reports').select('*');
      
      // If user is a salesman, only show their own reports
      if (activeUser && activeUser.role === 'salesman') {
        reportQuery = reportQuery.eq('salesman_id', activeUser.id);
      }
      
      const { data: reportData } = await reportQuery.order('created_at', { ascending: false });
      
      if (reportData) {
        // Fetch user names separately
        const { data: userData } = await supabase.from('users').select('id, name');
        const userMap = new Map(userData?.map(u => [u.id, u.name]) || []);
        
        const formattedReports: Report[] = reportData.map(r => ({
          id: r.id,
          customerId: r.customer_id || '',
          salesmanId: r.salesman_id || '',
          director: r.director || '',
          product: r.product || [],
          phone: r.phone || '',
          competitor: r.competitor || '',
          goal: r.goal || '',
          businessType: r.business_type || '',
          date: r.date || '',
          time: r.time || '',
          createdAt: r.created_at || new Date().toISOString(),
          salespersonName: userMap.get(r.salesman_id) || 'Unknown'
        }));
        setReports(formattedReports);
      }


    } catch (error: any) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };



  const saveReport = async (report: Omit<Report, 'id' | 'createdAt'>) => {
    try {
      if (!report.customerId || !report.salesmanId) {
        throw new Error('Customer and Salesman must be selected');
      }
      
      // Validate that customer_id and salesman_id are valid UUIDs
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(report.customerId)) {
        throw new Error('Invalid customer ID format');
      }
      if (!uuidRegex.test(report.salesmanId)) {
        throw new Error('Invalid salesman ID format');
      }
      
      const { error } = await supabase.from('reports').insert({
        customer_id: report.customerId,
        salesman_id: report.salesmanId,
        director: report.director,
        product: report.product,
        phone: report.phone,
        competitor: report.competitor,
        goal: report.goal,
        business_type: report.businessType,
        date: report.date,
        time: report.time
      });
      if (error) throw error;
      toast({ title: 'Success', description: 'Report saved successfully' });
      await fetchData();
      return true;
    } catch (error: any) {
      console.error('Save report error:', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return false;
    }
  };

  const saveCustomer = async (customer: Omit<Customer, 'id'>) => {
    try {
      if (!customer.businessType) {
        throw new Error('Business type is required');
      }
      
      const { error } = await supabase.from('customers').insert({
        business_name: customer.businessName,
        contact_person: customer.contactPerson,
        phone: customer.phone,
        email: customer.email,
        address: customer.address,
        business_type: customer.businessType,
        notes: customer.notes,
        salesman_id: customer.salesmanId
      });
      if (error) throw error;
      toast({ title: 'Success', description: 'Customer saved successfully' });
      await fetchData();
      return true;
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return false;
    }
  };

  const updateCustomer = async (id: string, customer: Omit<Customer, 'id'>) => {
    try {
      if (!customer.businessType) {
        throw new Error('Business type is required');
      }
      
      const { error } = await supabase.from('customers').update({
        business_name: customer.businessName,
        contact_person: customer.contactPerson,
        phone: customer.phone,
        email: customer.email,
        address: customer.address,
        business_type: customer.businessType,
        notes: customer.notes,
        salesman_id: customer.salesmanId
      }).eq('id', id);
      
      if (error) throw error;
      toast({ title: 'Success', description: 'Customer updated successfully' });
      await fetchData();
      return true;
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return false;
    }
  };

  const deleteCustomer = async (id: string) => {
    try {
      const { error } = await supabase.from('customers').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Success', description: 'Customer deleted successfully' });
      await fetchData();
      return true;
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return false;
    }
  };

  const deleteReport = async (id: string) => {
    try {
      const { error } = await supabase.from('reports').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Success', description: 'Report deleted successfully' });
      await fetchData();
      return true;
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return false;
    }
  };

  const saveAppointment = async () => true;
  const updateUserProfile = async () => true;

  useEffect(() => {
    const stored = localStorage.getItem('crm_current_user');
    if (stored) {
      const user = JSON.parse(stored);
      setCurrentUser(user);
      fetchData();
    }
  }, []);

  return (
    <AppContext.Provider value={{
      sidebarOpen, toggleSidebar, currentUser, customers, appointments, reports, currentView, setCurrentView,
      login, logout, saveCustomer, updateCustomer, deleteCustomer, fetchData, saveAppointment, saveReport,
      updateUserProfile, deleteReport, loading
    }}>
      {children}
    </AppContext.Provider>
  );
};